'use client';

import { useState } from 'react';
import Link from 'next/link';
import { useCommandCenterTool } from '@/lib/command-center/useCommandCenterTool';

type View = 'landing' | 'assessment' | 'results';

interface Question {
  id: string;
  text: string;
  section: string;
  weight: number;
}

interface Section {
  id: string;
  title: string;
  description: string;
  questions: Question[];
}

const sections: Section[] = [
  {
    id: 'applicability',
    title: 'Applicability & Scope',
    description: 'Determine if CCPA/CPRA applies to your organization',
    questions: [
      { id: 'ap1', text: 'Do you conduct business in California?', section: '1798.140(d)', weight: 10 },
      { id: 'ap2', text: 'Do you collect personal information of California residents?', section: '1798.140(d)', weight: 10 },
      { id: 'ap3', text: 'Have you determined if you meet revenue/data thresholds for CCPA applicability?', section: '1798.140(d)', weight: 9 },
      { id: 'ap4', text: 'Have you identified all categories of personal information you collect?', section: '1798.100', weight: 9 },
      { id: 'ap5', text: 'Do you process sensitive personal information as defined by CPRA?', section: '1798.140(ae)', weight: 8 },
    ],
  },
  {
    id: 'consumer-rights',
    title: 'Consumer Rights',
    description: 'Right to know, delete, correct, and opt-out (CCPA §1798.100-125)',
    questions: [
      { id: 'cr1', text: 'Can consumers request to know what personal information you collect about them?', section: '1798.100', weight: 10 },
      { id: 'cr2', text: 'Can consumers request deletion of their personal information?', section: '1798.105', weight: 10 },
      { id: 'cr3', text: 'Can consumers request correction of inaccurate personal information?', section: '1798.106', weight: 9 },
      { id: 'cr4', text: 'Can consumers opt-out of the sale or sharing of their personal information?', section: '1798.120', weight: 10 },
      { id: 'cr5', text: 'Can consumers limit the use of their sensitive personal information?', section: '1798.121', weight: 9 },
      { id: 'cr6', text: 'Do you respond to consumer requests within 45 days?', section: '1798.145', weight: 9 },
      { id: 'cr7', text: 'Do you have at least two methods for consumers to submit requests?', section: '1798.130(a)(1)', weight: 8 },
      { id: 'cr8', text: 'Do you verify consumer identity before fulfilling requests?', section: '1798.140', weight: 8 },
    ],
  },
  {
    id: 'privacy-notice',
    title: 'Privacy Notice Requirements',
    description: 'Notice and disclosure requirements (CCPA §1798.130)',
    questions: [
      { id: 'pn1', text: 'Do you have a comprehensive privacy policy accessible from your website?', section: '1798.130(a)(5)', weight: 10 },
      { id: 'pn2', text: 'Does your privacy policy disclose categories of PI collected in the past 12 months?', section: '1798.130(a)(5)(B)', weight: 9 },
      { id: 'pn3', text: 'Does your privacy policy disclose the purposes for collection?', section: '1798.130(a)(5)(B)', weight: 9 },
      { id: 'pn4', text: 'Does your privacy policy disclose categories of third parties with whom PI is shared?', section: '1798.130(a)(5)(C)', weight: 8 },
      { id: 'pn5', text: 'Do you disclose if you sell or share personal information?', section: '1798.130(a)(5)(D)', weight: 9 },
      { id: 'pn6', text: 'Do you disclose retention periods for each category of PI?', section: '1798.130(a)(5)(B)', weight: 8 },
      { id: 'pn7', text: 'Is your privacy policy updated at least annually?', section: '1798.130(a)(5)', weight: 7 },
      { id: 'pn8', text: 'Do you provide notice at or before the point of collection?', section: '1798.100(b)', weight: 9 },
    ],
  },
  {
    id: 'data-sales',
    title: 'Sale & Sharing of Data',
    description: 'Requirements for selling or sharing personal information (CCPA §1798.115-121)',
    questions: [
      { id: 'ds1', text: 'Do you have a "Do Not Sell or Share My Personal Information" link on your website?', section: '1798.135(a)(1)', weight: 10 },
      { id: 'ds2', text: 'Do you honor Global Privacy Control (GPC) signals?', section: '1798.135(b)(1)', weight: 9 },
      { id: 'ds3', text: 'Do you have a "Limit the Use of My Sensitive Personal Information" link?', section: '1798.135(a)(2)', weight: 9 },
      { id: 'ds4', text: 'Do you maintain opt-out requests for at least 12 months?', section: '1798.135', weight: 8 },
      { id: 'ds5', text: 'Do you have contracts with service providers/contractors addressing data use?', section: '1798.140(ag)', weight: 9 },
      { id: 'ds6', text: 'Do you disclose the specific categories of PI sold/shared?', section: '1798.115(c)', weight: 8 },
    ],
  },
  {
    id: 'security',
    title: 'Data Security',
    description: 'Reasonable security measures and breach notification',
    questions: [
      { id: 'sc1', text: 'Do you implement reasonable security measures to protect personal information?', section: '1798.150', weight: 10 },
      { id: 'sc2', text: 'Do you have a data breach notification process?', section: '1798.150', weight: 9 },
      { id: 'sc3', text: 'Is access to personal information limited to authorized personnel?', section: 'Best Practice', weight: 8 },
      { id: 'sc4', text: 'Do you encrypt personal information in transit and at rest?', section: 'Best Practice', weight: 8 },
      { id: 'sc5', text: 'Do you conduct regular security assessments?', section: 'CPRA 1798.185(a)(15)', weight: 8 },
      { id: 'sc6', text: 'Do you perform cybersecurity audits for high-risk processing?', section: 'CPRA 1798.185(a)(15)', weight: 7 },
    ],
  },
  {
    id: 'service-providers',
    title: 'Service Providers & Contractors',
    description: 'Requirements for third-party relationships (CCPA §1798.140)',
    questions: [
      { id: 'sp1', text: 'Do contracts with service providers prohibit selling/sharing personal information?', section: '1798.140(ag)(1)(A)', weight: 10 },
      { id: 'sp2', text: 'Do contracts require service providers to assist with consumer requests?', section: '1798.140(ag)(1)(C)', weight: 9 },
      { id: 'sp3', text: 'Do contracts prohibit retention beyond the business purpose?', section: '1798.140(ag)(1)(B)', weight: 8 },
      { id: 'sp4', text: 'Do you verify that service providers maintain reasonable security?', section: 'Best Practice', weight: 8 },
      { id: 'sp5', text: 'Do contracts allow you to assess service provider compliance?', section: '1798.140(ag)(1)(E)', weight: 7 },
      { id: 'sp6', text: 'Do you track all third parties receiving personal information?', section: '1798.115', weight: 8 },
    ],
  },
  {
    id: 'special-categories',
    title: 'Special Categories & Minors',
    description: 'Sensitive PI and requirements for minors (CCPA §1798.121, 1798.120)',
    questions: [
      { id: 'sm1', text: 'Do you limit use of sensitive PI to what is necessary for disclosed purposes?', section: '1798.121', weight: 9 },
      { id: 'sm2', text: 'If you collect data from minors under 16, do you obtain opt-in consent before selling?', section: '1798.120(c)', weight: 10 },
      { id: 'sm3', text: 'For minors under 13, do you obtain verifiable parental consent?', section: '1798.120(c)', weight: 10 },
      { id: 'sm4', text: 'Do you avoid using financial incentives that are unjust, coercive, or usurious?', section: '1798.125(b)(4)', weight: 8 },
      { id: 'sm5', text: 'If offering financial incentives, do you obtain opt-in consent?', section: '1798.125(b)(3)', weight: 8 },
    ],
  },
  {
    id: 'training',
    title: 'Training & Governance',
    description: 'Staff training and internal processes',
    questions: [
      { id: 'tr1', text: 'Are staff trained on CCPA/CPRA requirements and consumer rights?', section: '1798.130(a)(6)', weight: 9 },
      { id: 'tr2', text: 'Do you maintain records of consumer requests and responses?', section: '1798.130(a)(7)', weight: 8 },
      { id: 'tr3', text: 'Do you have a process for updating privacy practices when regulations change?', section: 'Best Practice', weight: 7 },
      { id: 'tr4', text: 'Have you conducted a risk assessment for automated decision-making?', section: 'CPRA 1798.185(a)(16)', weight: 7 },
      { id: 'tr5', text: 'Do you document purposes and retention periods for PI categories?', section: '1798.100(a)(3)', weight: 8 },
    ],
  },
];

export default function CCPACompliance() {
  const [view, setView] = useState<View>('landing');
  const [currentSection, setCurrentSection] = useState(0);
  const [answers, setAnswers] = useState<Record<string, 'yes' | 'no' | 'partial' | 'na'>>({});
  const [revenue, setRevenue] = useState<string>('');
  const [dataVolume, setDataVolume] = useState<string>('');
  const [saved, setSaved] = useState(false);

  const {
    enabled: commandCenterEnabled,
    saving,
    saveResult,
  } = useCommandCenterTool({
    toolId: 'ccpa-compliance',
    toolCategory: 'compliance',
  });

  const handleSaveToCommandCenter = async () => {
    const { score, gaps } = calculateScore();
    const sectionScores: Record<string, number> = {};
    sections.forEach((section) => {
      sectionScores[section.id] = getSectionScore(section.id);
    });

    const result = await saveResult({
      name: 'CCPA/CPRA Compliance Assessment',
      score_overall: score,
      scores: sectionScores,
      answers,
      gaps: gaps.map((g, i) => ({
        id: `gap-${i}`,
        title: g.question,
        severity: g.status === 'Not Implemented' ? 'high' : 'medium',
        description: `Reference: ${g.section}`,
      })),
      recommendations: score < 90 ? [
        { id: 'rec-1', title: 'Implement consumer rights mechanisms', priority: 'high' },
        { id: 'rec-2', title: 'Update privacy policy with required disclosures', priority: 'high' },
        { id: 'rec-3', title: 'Add Do Not Sell/Share and Limit Use links', priority: 'high' },
        { id: 'rec-4', title: 'Implement Global Privacy Control handling', priority: 'medium' },
        { id: 'rec-5', title: 'Review service provider contracts', priority: 'medium' },
      ] : [],
      metadata: { revenue, dataVolume },
    });

    if (result) setSaved(true);
  };

  const handleAnswer = (questionId: string, value: 'yes' | 'no' | 'partial' | 'na') => {
    setAnswers((prev) => ({ ...prev, [questionId]: value }));
  };

  const calculateScore = () => {
    let earned = 0;
    let possible = 0;
    const gaps: { section: string; question: string; status: string }[] = [];

    sections.forEach((section) => {
      section.questions.forEach((q) => {
        const answer = answers[q.id];
        if (answer === 'na') return;

        possible += q.weight;
        if (answer === 'yes') {
          earned += q.weight;
        } else if (answer === 'partial') {
          earned += q.weight * 0.5;
          gaps.push({ section: q.section, question: q.text, status: 'Partial' });
        } else if (answer === 'no') {
          gaps.push({ section: q.section, question: q.text, status: 'Not Implemented' });
        }
      });
    });

    return {
      score: possible > 0 ? Math.round((earned / possible) * 100) : 0,
      gaps: gaps.sort((a, b) => a.section.localeCompare(b.section)),
    };
  };

  const getSectionScore = (sectionId: string) => {
    const section = sections.find((s) => s.id === sectionId);
    if (!section) return 0;

    let earned = 0;
    let possible = 0;

    section.questions.forEach((q) => {
      const answer = answers[q.id];
      if (answer === 'na') return;
      possible += q.weight;
      if (answer === 'yes') earned += q.weight;
      else if (answer === 'partial') earned += q.weight * 0.5;
    });

    return possible > 0 ? Math.round((earned / possible) * 100) : 0;
  };

  const getComplianceLevel = (score: number) => {
    if (score >= 90) return { level: 'Compliant', color: 'text-green-400', bg: 'bg-green-500/20' };
    if (score >= 70) return { level: 'Substantially Compliant', color: 'text-yellow-400', bg: 'bg-yellow-500/20' };
    if (score >= 50) return { level: 'Partially Compliant', color: 'text-accent-400', bg: 'bg-accent-500/20' };
    return { level: 'Not Compliant', color: 'text-red-400', bg: 'bg-red-500/20' };
  };

  const exportToMarkdown = () => {
    const { score, gaps } = calculateScore();
    const compliance = getComplianceLevel(score);

    let md = `# CCPA/CPRA Compliance Assessment Report\n\n`;
    md += `**Generated:** ${new Date().toLocaleDateString()}\n\n`;
    md += `**Revenue Threshold:** ${revenue || 'Not specified'}\n`;
    md += `**Data Volume:** ${dataVolume || 'Not specified'}\n\n`;
    md += `## Overall Compliance Score\n\n`;
    md += `**Score:** ${score}%\n`;
    md += `**Status:** ${compliance.level}\n\n`;

    md += `## Section Scores\n\n`;
    md += `| Section | Score |\n`;
    md += `|---------|-------|\n`;
    sections.forEach((section) => {
      md += `| ${section.title} | ${getSectionScore(section.id)}% |\n`;
    });

    if (gaps.length > 0) {
      md += `\n## Compliance Gaps\n\n`;
      md += `| Reference | Issue | Status |\n`;
      md += `|-----------|-------|--------|\n`;
      gaps.forEach((gap) => {
        md += `| ${gap.section} | ${gap.question} | ${gap.status} |\n`;
      });
    }

    md += `\n## CCPA/CPRA Applicability Thresholds\n\n`;
    md += `CCPA applies to for-profit entities doing business in California that meet ANY of:\n`;
    md += `- Annual gross revenue exceeding $25 million\n`;
    md += `- Buy, sell, or share PI of 100,000+ consumers/households annually\n`;
    md += `- Derive 50%+ of annual revenue from selling/sharing consumers' PI\n\n`;

    md += `## Key CPRA Enhancements\n\n`;
    md += `- Created California Privacy Protection Agency (CPPA)\n`;
    md += `- Added right to correction\n`;
    md += `- Added right to limit sensitive PI use\n`;
    md += `- Expanded "sale" to include "sharing" for cross-context advertising\n`;
    md += `- Required risk assessments for high-risk processing\n`;
    md += `- Removed 30-day cure period for violations\n\n`;

    md += `## Penalties\n\n`;
    md += `- **Administrative fines:** Up to $7,500 per intentional violation\n`;
    md += `- **Unintentional violations:** Up to $2,500 per violation\n`;
    md += `- **Data breaches:** $100-$750 per consumer per incident (private right of action)\n\n`;

    md += `## Recommended Actions\n\n`;
    if (score < 90) {
      md += `1. Implement all required consumer rights mechanisms\n`;
      md += `2. Update privacy policy with all required disclosures\n`;
      md += `3. Add "Do Not Sell or Share" and "Limit Use" links\n`;
      md += `4. Implement Global Privacy Control (GPC) signal handling\n`;
      md += `5. Review and update service provider contracts\n`;
      md += `6. Train staff on consumer request handling\n`;
    } else {
      md += `1. Maintain current compliance posture\n`;
      md += `2. Monitor CPPA regulations and guidance\n`;
      md += `3. Continue annual privacy policy updates\n`;
      md += `4. Perform regular training refreshers\n`;
    }

    md += `\n---\n*Assessment conducted using The Art of CTO CCPA/CPRA Compliance Checker*\n`;

    const blob = new Blob([md], { type: 'text/markdown' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `ccpa-cpra-assessment-${new Date().toISOString().split('T')[0]}.md`;
    a.click();
    URL.revokeObjectURL(url);
  };

  if (view === 'landing') {
    return (
      <div className="min-h-screen bg-background">
        <div className="max-w-4xl mx-auto px-4 py-12">
          <Link href="/tools" className="text-foreground-tertiary hover:text-accent mb-8 inline-block">
            ← Back to Tools
          </Link>

          <div className="text-center mb-12">
            <span className="text-accent text-sm font-medium tracking-wider uppercase">
              California Privacy
            </span>
            <h1 className="text-4xl font-bold text-foreground mt-2 mb-4">
              CCPA/CPRA Compliance Checker
            </h1>
            <p className="text-foreground-secondary text-lg max-w-2xl mx-auto">
              Assess your compliance with the California Consumer Privacy Act (CCPA) as amended
              by the California Privacy Rights Act (CPRA). Identify gaps and protect consumer rights.
            </p>
          </div>

          <div className="grid md:grid-cols-2 gap-6 mb-12">
            <div className="bg-surface border border-border-muted rounded-lg p-6">
              <h3 className="text-foreground font-semibold mb-3">What is CCPA/CPRA?</h3>
              <p className="text-foreground-secondary text-sm">
                The California Consumer Privacy Act (CCPA), as amended by the California
                Privacy Rights Act (CPRA), gives California consumers rights over their
                personal information and imposes obligations on businesses that collect it.
              </p>
            </div>
            <div className="bg-surface border border-border-muted rounded-lg p-6">
              <h3 className="text-foreground font-semibold mb-3">CPRA Effective Date</h3>
              <p className="text-foreground-secondary text-sm">
                CPRA became operative January 1, 2023, with enforcement beginning July 1, 2023.
                It applies to personal information collected on or after January 1, 2022.
                The CPPA now enforces the law.
              </p>
            </div>
          </div>

          <div className="bg-surface border border-border-muted rounded-lg p-6 mb-8">
            <h3 className="text-foreground font-semibold mb-4">Check Applicability</h3>

            <div className="mb-4">
              <label className="text-foreground-secondary text-sm block mb-2">
                What is your annual gross revenue?
              </label>
              <select
                value={revenue}
                onChange={(e) => setRevenue(e.target.value)}
                className="w-full bg-background border border-border rounded-lg px-4 py-2 text-foreground"
              >
                <option value="">Select range...</option>
                <option value="under-25m">Under $25 million</option>
                <option value="over-25m">$25 million or more</option>
              </select>
            </div>

            <div>
              <label className="text-foreground-secondary text-sm block mb-2">
                How much California consumer data do you process?
              </label>
              <select
                value={dataVolume}
                onChange={(e) => setDataVolume(e.target.value)}
                className="w-full bg-background border border-border rounded-lg px-4 py-2 text-foreground"
              >
                <option value="">Select range...</option>
                <option value="under-100k">Under 100,000 consumers/households/devices</option>
                <option value="over-100k">100,000+ consumers/households/devices</option>
              </select>
            </div>

            {(revenue === 'over-25m' || dataVolume === 'over-100k') && (
              <div className="mt-4 p-3 rounded-lg bg-accent-muted border border-border">
                <p className="text-sm text-foreground">
                  <span className="text-accent font-semibold">CCPA likely applies</span> -
                  Your organization likely meets the applicability thresholds.
                </p>
              </div>
            )}
          </div>

          <div className="bg-surface border border-border-muted rounded-lg p-6 mb-8">
            <h3 className="text-foreground font-semibold mb-3">Consumer Rights Under CCPA/CPRA</h3>
            <div className="grid md:grid-cols-2 gap-4 text-sm text-foreground-secondary">
              <div>
                <p className="mb-2"><span className="text-accent">1.</span> Right to Know (access)</p>
                <p className="mb-2"><span className="text-accent">2.</span> Right to Delete</p>
                <p className="mb-2"><span className="text-accent">3.</span> Right to Correct</p>
                <p className="mb-2"><span className="text-accent">4.</span> Right to Opt-Out of Sale/Sharing</p>
              </div>
              <div>
                <p className="mb-2"><span className="text-accent">5.</span> Right to Limit Sensitive PI Use</p>
                <p className="mb-2"><span className="text-accent">6.</span> Right to Data Portability</p>
                <p className="mb-2"><span className="text-accent">7.</span> Right to Non-Discrimination</p>
                <p className="mb-2"><span className="text-accent">8.</span> Right to Opt-Out of Automated Decision-Making</p>
              </div>
            </div>
          </div>

          <div className="bg-surface border border-border rounded-lg p-6 mb-8">
            <h3 className="text-accent font-semibold mb-2">Enforcement</h3>
            <p className="text-foreground-secondary text-sm">
              The California Privacy Protection Agency (CPPA) enforces CCPA/CPRA.
              The 30-day cure period for violations has been eliminated.
              Penalties range from $2,500 for unintentional to $7,500 for intentional violations.
            </p>
          </div>

          <div className="text-center">
            <button
              onClick={() => setView('assessment')}
              className="bg-accent hover:bg-accent-hover text-accent-foreground font-semibold px-8 py-3 rounded-lg transition-colors"
            >
              Start Compliance Assessment
            </button>
            <p className="text-foreground-tertiary text-sm mt-4">
              Takes approximately 15-20 minutes to complete
            </p>
          </div>
        </div>
      </div>
    );
  }

  if (view === 'assessment') {
    const section = sections[currentSection];
    const progress = ((currentSection + 1) / sections.length) * 100;

    return (
      <div className="min-h-screen bg-background">
        <div className="max-w-4xl mx-auto px-4 py-12">
          <div className="flex items-center justify-between mb-8">
            <button
              onClick={() => setView('landing')}
              className="text-foreground-tertiary hover:text-accent"
            >
              ← Back
            </button>
            <span className="text-foreground-tertiary text-sm">
              Section {currentSection + 1} of {sections.length}
            </span>
          </div>

          <div className="mb-8">
            <div className="flex justify-between text-sm text-foreground-tertiary mb-2">
              <span>{section.title}</span>
              <span>{Math.round(progress)}%</span>
            </div>
            <div className="h-2 bg-cream/10 rounded-full overflow-hidden">
              <div
                className="h-full bg-accent transition-all duration-300"
                style={{ width: `${progress}%` }}
              />
            </div>
          </div>

          <div className="bg-surface border border-border-muted rounded-lg p-6 mb-6">
            <h2 className="text-xl font-semibold text-foreground mb-2">{section.title}</h2>
            <p className="text-foreground-tertiary">{section.description}</p>
          </div>

          <div className="space-y-4">
            {section.questions.map((question) => (
              <div
                key={question.id}
                className="bg-surface border border-border-muted rounded-lg p-4"
              >
                <div className="flex items-start justify-between gap-4 mb-3">
                  <div>
                    <span className="text-accent text-xs font-medium">
                      {question.section}
                    </span>
                    <p className="text-foreground mt-1">{question.text}</p>
                  </div>
                </div>
                <div className="flex gap-2">
                  {(['yes', 'partial', 'no', 'na'] as const).map((value) => (
                    <button
                      key={value}
                      onClick={() => handleAnswer(question.id, value)}
                      className={`px-4 py-2 rounded text-sm font-medium transition-colors ${
                        answers[question.id] === value
                          ? value === 'yes'
                            ? 'bg-green-500/20 text-green-400 border border-green-500/50'
                            : value === 'partial'
                            ? 'bg-yellow-500/20 text-yellow-400 border border-yellow-500/50'
                            : value === 'no'
                            ? 'bg-red-500/20 text-red-400 border border-red-500/50'
                            : 'bg-gray-500/20 text-gray-400 border border-gray-500/50'
                          : 'bg-cream/5 text-foreground-tertiary border border-border-muted hover:border-border/30'
                      }`}
                    >
                      {value === 'na' ? 'N/A' : value.charAt(0).toUpperCase() + value.slice(1)}
                    </button>
                  ))}
                </div>
              </div>
            ))}
          </div>

          <div className="flex justify-between mt-8">
            <button
              onClick={() => setCurrentSection(Math.max(0, currentSection - 1))}
              disabled={currentSection === 0}
              className="px-6 py-2 rounded-lg border border-border text-foreground disabled:opacity-50 disabled:cursor-not-allowed hover:border-border/40 transition-colors"
            >
              Previous
            </button>
            {currentSection < sections.length - 1 ? (
              <button
                onClick={() => setCurrentSection(currentSection + 1)}
                className="bg-accent hover:bg-accent-hover text-accent-foreground font-semibold px-6 py-2 rounded-lg transition-colors"
              >
                Next Section
              </button>
            ) : (
              <button
                onClick={() => setView('results')}
                className="bg-accent hover:bg-accent-hover text-accent-foreground font-semibold px-6 py-2 rounded-lg transition-colors"
              >
                View Results
              </button>
            )}
          </div>
        </div>
      </div>
    );
  }

  const { score, gaps } = calculateScore();
  const compliance = getComplianceLevel(score);

  return (
    <div className="min-h-screen bg-background">
      <div className="max-w-4xl mx-auto px-4 py-12">
        <div className="flex items-center justify-between mb-8">
          <button
            onClick={() => setView('assessment')}
            className="text-foreground-tertiary hover:text-accent"
          >
            ← Back to Assessment
          </button>
          <button
            onClick={exportToMarkdown}
            className="bg-cream/10 hover:bg-cream/20 text-foreground px-4 py-2 rounded-lg transition-colors"
          >
            Export Report
          </button>
        </div>

        <div className="text-center mb-12">
          <h1 className="text-3xl font-bold text-foreground mb-4">
            CCPA/CPRA Compliance Results
          </h1>
          <div className={`inline-block px-6 py-3 rounded-lg ${compliance.bg}`}>
            <span className={`text-4xl font-bold ${compliance.color}`}>{score}%</span>
            <p className={`${compliance.color} font-medium mt-1`}>{compliance.level}</p>
          </div>
        </div>

        <div className="bg-surface border border-border-muted rounded-lg p-6 mb-8">
          <h2 className="text-xl font-semibold text-foreground mb-4">Section Scores</h2>
          <div className="space-y-3">
            {sections.map((section) => {
              const sectionScore = getSectionScore(section.id);
              const sectionCompliance = getComplianceLevel(sectionScore);
              return (
                <div key={section.id}>
                  <div className="flex justify-between text-sm mb-1">
                    <span className="text-foreground-secondary">{section.title}</span>
                    <span className={sectionCompliance.color}>{sectionScore}%</span>
                  </div>
                  <div className="h-2 bg-cream/10 rounded-full overflow-hidden">
                    <div
                      className={`h-full transition-all ${
                        sectionScore >= 90
                          ? 'bg-green-500'
                          : sectionScore >= 70
                          ? 'bg-yellow-500'
                          : sectionScore >= 50
                          ? 'bg-accent'
                          : 'bg-red-500'
                      }`}
                      style={{ width: `${sectionScore}%` }}
                    />
                  </div>
                </div>
              );
            })}
          </div>
        </div>

        {gaps.length > 0 && (
          <div className="bg-surface border border-border-muted rounded-lg p-6 mb-8">
            <h2 className="text-xl font-semibold text-foreground mb-4">
              Compliance Gaps ({gaps.length})
            </h2>
            <div className="space-y-3 max-h-96 overflow-y-auto">
              {gaps.map((gap, index) => (
                <div
                  key={index}
                  className="border border-border-muted rounded-lg p-3"
                >
                  <div className="flex items-start justify-between gap-4">
                    <div>
                      <span className="text-accent text-xs font-medium">
                        {gap.section}
                      </span>
                      <p className="text-foreground text-sm mt-1">{gap.question}</p>
                    </div>
                    <span
                      className={`text-xs font-medium px-2 py-1 rounded whitespace-nowrap ${
                        gap.status === 'Partial'
                          ? 'bg-yellow-500/20 text-yellow-400'
                          : 'bg-red-500/20 text-red-400'
                      }`}
                    >
                      {gap.status}
                    </span>
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}

        <div className="bg-surface border border-border-muted rounded-lg p-6 mb-8">
          <h2 className="text-xl font-semibold text-foreground mb-4">Required Website Links</h2>
          <div className="text-foreground-secondary text-sm space-y-3">
            <div className="p-3 bg-cream/5 rounded-lg">
              <p className="text-foreground font-medium mb-1">&quot;Do Not Sell or Share My Personal Information&quot;</p>
              <p>Required if you sell or share PI for cross-context behavioral advertising</p>
            </div>
            <div className="p-3 bg-cream/5 rounded-lg">
              <p className="text-foreground font-medium mb-1">&quot;Limit the Use of My Sensitive Personal Information&quot;</p>
              <p>Required if you use sensitive PI beyond disclosed purposes</p>
            </div>
            <div className="p-3 bg-cream/5 rounded-lg">
              <p className="text-foreground font-medium mb-1">Global Privacy Control (GPC)</p>
              <p>Must be honored as a valid opt-out signal</p>
            </div>
          </div>
        </div>

        <div className="bg-surface border border-border rounded-lg p-6">
          <h2 className="text-xl font-semibold text-foreground mb-4">Penalties</h2>
          <div className="text-foreground-secondary text-sm space-y-2">
            <p><span className="text-red-400 font-medium">$7,500</span> per intentional violation</p>
            <p><span className="text-yellow-400 font-medium">$7,500</span> per violation involving minor&apos;s data</p>
            <p><span className="text-accent font-medium">$2,500</span> per unintentional violation</p>
            <p className="mt-4 text-foreground-tertiary">
              Private right of action for data breaches: $100-$750 per consumer per incident,
              or actual damages if greater.
            </p>
          </div>
        </div>

        <div className="flex flex-wrap gap-4 justify-center mt-8">
          <button
            onClick={exportToMarkdown}
            className="px-6 py-3 bg-cream/10 hover:bg-cream/20 text-foreground rounded-lg transition-colors"
          >
            Export Report
          </button>
          {commandCenterEnabled && (
            <button
              onClick={handleSaveToCommandCenter}
              disabled={saving || saved}
              className="px-6 py-3 bg-blue-600 text-white rounded-lg font-medium hover:bg-blue-700 transition-colors disabled:opacity-50"
            >
              {saving ? 'Saving...' : saved ? 'Saved to Command Center' : 'Save to Command Center'}
            </button>
          )}
          <button
            onClick={() => {
              setView('landing');
              setCurrentSection(0);
              setAnswers({});
              setSaved(false);
            }}
            className="px-6 py-3 border border-border rounded-lg text-foreground hover:border-accent transition-colors"
          >
            Start Over
          </button>
        </div>
      </div>
    </div>
  );
}
